/**
 * Schema entries for the "Schedule details" section of the Details step.
 *
 * Covers: calendar row → duration → find time → date/time picker
 * (manual/adhoc) → buffer → calendar visibility → location → HackerRank
 * template → candidate phone → host → office → note taker.
 *
 * Pure builder function — receives all dependencies as args, returns
 * schema entries. No React hooks called here.
 */
import type { ReactNode } from 'react';
import moment from 'moment-timezone';
import { ScheduleCalendarRowField } from '../../fields/ScheduleCalendarRowField';
import { CandidatePhoneField } from '../../fields/CandidatePhoneField';
import {
  DateTimeField,
  DateRangeFields,
  parse12hrTime,
  normalizeTo12h,
  timeSelectFilterOption,
  isNewTimeOption,
} from '../../fields/dateTime';
import { BufferField, FindTimeField } from '../../../../calendar/selfSchedule';
import { DurationDecrementGate } from '../../fields/DurationDecrementGate';
import { copy, isDateTimeEditingCondition } from './constants';
import type { ScheduleCalendarData } from '../useScheduleCalendarData';
import type { FindTimeFieldBag, BufferFieldBag } from './types';

interface ScheduleDetailsSchemaDeps {
  isManualOrAdhoc: boolean;
  isSelfSchedule: boolean;
  isBulkSelfSchedule: boolean;
  apiReference: string;
  calendarData: ScheduleCalendarData;
  shouldShowCalendarRow: boolean;
  durationField: Record<string, unknown>;
  locationField: Record<string, unknown>;
  hostField: Record<string, unknown>;
  officeField: Record<string, unknown>;
  noteTakerField: Record<string, unknown>;
  hackerRankTemplateOptions: Array<{ value: string; label: string }>;
  hrLoading: boolean;
  startTimeOptions: Array<{ value: string; label: string }>;
  endTimeOptions: Array<{ value: string; label: string }>;
  datePickerMinDate: Date;
  /** End-date picker floor — start day + 1 (multi-day ends later). */
  endDatePickerMinDate: Date;
  /** Wizard `onFieldChange` — routes custom date-row edits through the
   *  calendar/slot sync (`useFormCalendarSync`). */
  onDateFieldChange?: (
    _name: string,
    _value: unknown,
    _allValues: Record<string, unknown>
  ) => void;
  candidateName?: string;
  candidateAvatar?: string;
  candidatePhone?: string;
  findTimeProps?: FindTimeFieldBag;
  bufferProps?: BufferFieldBag;
}

export function buildScheduleDetailsSchema({
  isManualOrAdhoc,
  isSelfSchedule,
  isBulkSelfSchedule,
  apiReference,
  calendarData,
  shouldShowCalendarRow,
  durationField,
  locationField,
  hostField,
  officeField,
  noteTakerField,
  hackerRankTemplateOptions,
  hrLoading,
  startTimeOptions,
  endTimeOptions,
  datePickerMinDate,
  endDatePickerMinDate,
  onDateFieldChange,
  candidateName,
  candidateAvatar,
  candidatePhone,
  findTimeProps,
  bufferProps,
}: ScheduleDetailsSchemaDeps): unknown[] {
  // Outlook organizer hint — single source for both the read-only
  // strip (info icon) AND the edit Select (label tooltip). Legacy
  // (`getCalendarLabelInfoText`) gates this ONLY on the connected
  // calendar being Outlook — NOT on primary — and resolves the owner
  // name via the users list. `calendarData.outlookOwner` now carries
  // that live resolution (recomputed when the picker swaps calendars),
  // so the duplicate `liveSelected`-based recompute is gone.
  const outlookHint =
    calendarData.isOutlook && calendarData.outlookOwner
      ? `In Outlook, this interview will display ${calendarData.outlookOwner.name} as the organizer as they own this calendar.`
      : undefined;

  // SCH-70 — Outlook can't create a private event unless the API caller
  // owns the target calendar, so the Private toggle is locked OFF for
  // self-schedule and for shared calendars the recruiter doesn't own.
  // `privateLockReason` resolves the case (null = free); DetailsForm's
  // force-off effect keeps the value `false` so the payload ships
  // `calendar_visibility: 'default'`.
  const privateLockReason = calendarData.privateLockReason;
  const privateLockTooltip =
    privateLockReason === 'self-schedule'
      ? copy.calendarVisibility.lockedSelfSchedule
      : privateLockReason === 'shared-calendar'
        ? copy.calendarVisibility.lockedSharedCalendar
        : undefined;

  return [
    {
      type: 'sectionLabel',
      label: copy.scheduleSectionLabel,
    },

    // Calendar row — first field under "Schedule details".
    // Two FormBuilder entries share the `isCalendarEditing` toggle:
    //   - Custom read-only strip (view mode)
    //   - Native select (edit mode, writes structured `calendar_info`)
    ...(shouldShowCalendarRow
      ? [
          {
            type: 'input' as const,
            name: 'isCalendarEditing',
            hidden: true,
          },
          {
            type: 'custom' as const,
            name: 'calendar_info',
            condition: {
              operator: 'notEqual' as const,
              field: 'isCalendarEditing',
              value: 'edit',
              preserveValue: true,
            },
            render: ({
              field,
              formMethods,
            }: {
              field: { value: unknown };
              formMethods: {
                setValue: (_n: string, _v: unknown, _o?: unknown) => void;
                watch: (_n: string) => unknown;
              };
            }) => {
              const info = field.value as
                | { provider_calendar_id?: string }
                | null
                | undefined;
              // Resolve summary from the LIVE form value — `calendarData.summaryName`
              // is computed without `calendarInfo`, so it always falls back to the
              // user's primary and never reflects the plan's selection.
              const liveId = info?.provider_calendar_id;
              const liveSelected = liveId
                ? calendarData.findById(liveId)
                : undefined;
              const summaryName =
                liveSelected?.name ?? calendarData.summaryName;
              const showYouSuffix = liveSelected
                ? Boolean(liveSelected.is_primary)
                : calendarData.showYouSuffix;
              return (
                <ScheduleCalendarRowField
                  summaryName={summaryName}
                  showYouSuffix={showYouSuffix}
                  isLoading={calendarData.isLoading}
                  hideEdit={calendarData.hideEdit}
                  disabledTooltip={calendarData.disabledTooltip}
                  isCalConnected={calendarData.isCalConnected}
                  calendarInfoId={info?.provider_calendar_id}
                  calendars={calendarData.calendars}
                  defaultCalendar={calendarData.defaultCalendar}
                  isEdit={apiReference === 'edit'}
                  refetchUuid={calendarData.refetchUuid}
                  outlookHint={outlookHint}
                  formMethods={formMethods}
                />
              );
            },
          },
          {
            type: 'select' as const,
            name: 'calendar_info_picker',
            label: copy.calendar.label,
            // Legacy parity: the Select carried the organizer hint as
            // its label `labelInfoText`. FormBuilder's `tooltip` renders
            // the same info icon next to the label, so the hint stays
            // visible while editing the calendar and recomputes live as
            // `outlookOwner` re-derives on each pick.
            ...(outlookHint ? { tooltip: outlookHint } : {}),
            options: calendarData.selectOptions,
            condition: {
              operator: 'equal' as const,
              field: 'isCalendarEditing',
              value: 'edit',
              preserveValue: true,
            },
            fieldProps: {
              loading: calendarData.isLoading,
              searchable: false,
            },
            onChange: (
              value: unknown,
              formMethods: {
                setValue: (_n: string, _v: unknown, _o?: unknown) => void;
              }
            ) => {
              const id = Array.isArray(value)
                ? value[0]
                : (value as string | undefined);
              if (typeof id !== 'string' || !id) return;
              const cal = calendarData.findById(id);
              formMethods.setValue('calendar_info', {
                provider_calendar_id: id,
                is_primary: Boolean(cal?.is_primary),
              });
            },
          },
        ]
      : []),

    {
      ...durationField,
      required: true,
      validate: (value: unknown) => {
        if (typeof value !== 'number') {
          return 'Duration is required';
        }
        if (value < 5) {
          return 'Duration must be at least 5 minutes';
        }
        if (value > 1440) {
          return 'Duration must be at most 24 hours';
        }
        return true;
      },
      wrapper: (node: ReactNode) => (
        <DurationDecrementGate>{node}</DurationDecrementGate>
      ),
    },

    // Find Time — self-schedule only. Hidden until an interviewer is picked.
    ...(isSelfSchedule && findTimeProps
      ? [
          {
            type: 'custom' as const,
            name: 'findTime',
            render: ({
              formMethods,
            }: {
              formMethods: { watch: (_n: string) => unknown };
            }) => {
              const raw = formMethods.watch('participant_ids');
              const ids: Array<string | number> = Array.isArray(raw)
                ? (raw as Array<string | number>)
                : raw
                  ? [raw as string | number]
                  : [];
              if (ids.length === 0) return null;
              return (
                <FindTimeField
                  value={findTimeProps.value}
                  slotsCount={findTimeProps.slotsCount}
                  timezone={findTimeProps.timezone}
                  minimumNoticeHours={findTimeProps.minimumNoticeHours}
                  availableDays={findTimeProps.availableDays}
                  onApply={findTimeProps.onApply}
                  onClear={findTimeProps.onClear}
                  onPreview={findTimeProps.onPreview}
                  onNoAvailability={findTimeProps.onNoAvailability}
                />
              );
            },
          },
        ]
      : []),

    // Date/Time picker — manual/adhoc only.
    // `isDateTimeEditing` hidden field toggles between the read-only
    // strip and the date + time selects.
    ...(isManualOrAdhoc
      ? [
          {
            type: 'input' as const,
            name: 'isDateTimeEditing',
            hidden: true,
          },
          {
            type: 'custom' as const,
            name: 'dateTime',
            render: ({
              field,
              formMethods,
            }: {
              field: { value: unknown };
              formMethods: {
                setValue: (_n: string, _v: unknown) => void;
                watch: (_n: string) => unknown;
              };
            }) => {
              const mode = formMethods.watch('isDateTimeEditing');
              if (mode === 'edit') return null;
              return (
                <DateTimeField
                  value={typeof field.value === 'string' ? field.value : ''}
                  onEdit={() =>
                    formMethods.setValue('isDateTimeEditing', 'edit')
                  }
                />
              );
            },
          },
          // Start date (+ End date for multi-day) as ONE custom field.
          // A flex layout gives "full width when alone, side by side when
          // both" — impossible with the FormBuilder grid + a single
          // field, and a two-field grid either gaps (same-day) or can't
          // collapse. Both dates stay single form values (`startDate` /
          // `endDate`) so they round-trip on edit; changes route through
          // `onDateFieldChange` to run the same calendar/slot sync.
          {
            type: 'custom' as const,
            name: 'dateRange',
            condition: isDateTimeEditingCondition,
            render: ({
              formMethods,
            }: {
              formMethods: React.ComponentProps<
                typeof DateRangeFields
              >['formMethods'];
            }) => (
              <DateRangeFields
                formMethods={formMethods}
                onFieldChange={onDateFieldChange}
                startMinDate={datePickerMinDate}
                endMinDate={endDatePickerMinDate}
              />
            ),
          },
          {
            type: 'section' as const,
            columns: 2,
            condition: isDateTimeEditingCondition,
            fields: [
              {
                type: 'select' as const,
                name: 'startTime',
                label: 'Start time',
                required: 'Start time is required',
                options: startTimeOptions,
                colSpan: 1,
                // Creatable lets recruiters type an odd minute (04:43)
                // the 15-min grid omits — parity with legacy prod.
                // `createOptionValue` (kula 1.10.85) canonicalizes the
                // committed value to "hh:mm A", so value↔option stay
                // synced (no raw-string desync) and `required` + prefill
                // keep working. kula FormBuilder reads the Select variant
                // from `fieldProps.selectType`.
                fieldProps: {
                  selectType: 'creatable',
                  searchable: true,
                  createOptionValue: (input: string) => normalizeTo12h(input),
                  // Suppress the "Create …" entry when the normalized
                  // value already exists as an option (grid or the
                  // injected current value) — prevents the duplicate
                  // "03:33 AM"; the user just picks the existing one.
                  isValidNewOption: isNewTimeOption,
                  formatCreateLabel: (input: string) =>
                    normalizeTo12h(input) ?? input,
                  // kula hardcodes `filterOption: undefined` (default
                  // substring filter) for creatable; that drops the
                  // "Create…" entry because its label is the normalized
                  // value (03:33 AM) which doesn't substring-match the
                  // raw input (333) → "No options". `reactSelectProps`
                  // spreads last in kula's <CreatableSelect>, so this
                  // wins. `timeSelectFilterOption` keeps the
                  // normalized/create option + does normal matching.
                  reactSelectProps: { filterOption: timeSelectFilterOption },
                },
              },
              {
                type: 'select' as const,
                name: 'endTime',
                label: 'End time',
                required: 'End time is required',
                options: endTimeOptions,
                colSpan: 1,
                fieldProps: {
                  selectType: 'creatable',
                  searchable: true,
                  createOptionValue: (input: string) => normalizeTo12h(input),
                  // Suppress the "Create …" entry when the normalized
                  // value already exists as an option (grid or the
                  // injected current value) — prevents the duplicate
                  // "03:33 AM"; the user just picks the existing one.
                  isValidNewOption: isNewTimeOption,
                  formatCreateLabel: (input: string) =>
                    normalizeTo12h(input) ?? input,
                  reactSelectProps: { filterOption: timeSelectFilterOption },
                },
                validate: (
                  value: unknown,
                  allValues: Record<string, unknown>
                ) => {
                  if (!value || !allValues.startTime) return true;
                  const start = parse12hrTime(allValues.startTime as string);
                  const end = parse12hrTime(value as string);
                  if (!start || !end) return true;
                  // Compare full datetimes (date + time), not minutes of
                  // day — a slot ending 02:45 PM on a LATER day is valid
                  // even though 02:45 PM < 03:15 PM as clock times. End
                  // date falls back to start date for same-day slots.
                  const startDate =
                    (allValues.startDate as Date | null) ?? null;
                  const endDate =
                    (allValues.endDate as Date | null) ?? startDate;
                  if (startDate && endDate) {
                    const startAt = moment(startDate)
                      .set({
                        hour: start.hours,
                        minute: start.minutes,
                        second: 0,
                        millisecond: 0,
                      })
                      .valueOf();
                    const endAt = moment(endDate)
                      .set({
                        hour: end.hours,
                        minute: end.minutes,
                        second: 0,
                        millisecond: 0,
                      })
                      .valueOf();
                    if (endAt <= startAt) return 'End must be after start';
                    return true;
                  }
                  // No dates yet (pre-hydration) — fall back to clock
                  // comparison so same-day editing still guards end<=start.
                  const startMin = start.hours * 60 + start.minutes;
                  const endMin = end.hours * 60 + end.minutes;
                  if (endMin <= startMin)
                    return 'End time must be after start time';
                  return true;
                },
              },
            ],
          },
        ]
      : []),

    // Buffer time — all modes (self-schedule, manual, adhoc) + edit,
    // behind feInterviewBuffer flag. `bufferProps` is already
    // flag-gated by DetailsStep (kula/core CORE-4352 supports buffer
    // on both `/ats_schedules` and `/ats_interviews`). Placed below the
    // Find Time / date-time block — buffer is padding around the chosen
    // time, so it reads after the time is set.
    ...(bufferProps
      ? [
          {
            type: 'custom' as const,
            name: 'buffer',
            render: () => (
              <BufferField
                value={bufferProps.value}
                onChange={bufferProps.onChange}
              />
            ),
          },
        ]
      : []),

    {
      type: 'switch' as const,
      name: 'calendar_visibility',
      disabled: privateLockTooltip !== undefined,
      fieldProps: {
        switchLabel: copy.calendarVisibility.label,
        description: copy.calendarVisibility.subLabel,
        fullWidth: true,
        labelAlign: 'center',
        labelPosition: 'left',
        // Locked-off look (SCH-70): non-interactive toggle + a tooltip on
        // hover explaining why, with the label kept readable so the user
        // can still see what's locked. Mirrors the note-taker mandatory
        // lock (`useNoteTakerConfig`), inverted to OFF.
        ...(privateLockTooltip !== undefined
          ? {
              disabledTooltip: privateLockTooltip,
              dimLabelWhenDisabled: false,
            }
          : {}),
      },
    },

    locationField,

    {
      type: 'select' as const,
      name: 'ext_interview_template_id',
      label: 'HackerRank Template',
      placeholder: 'Select template',
      options: hackerRankTemplateOptions,
      loading: hrLoading,
      // Required ONLY when the conditional fires (location=hackerrank) —
      // FormBuilder's schemaGenerator wraps conditional fields in
      // `union([undefined, schema])` and runs the required check inside
      // the `superRefine`. Mirrors legacy
      // `useSchLocationField`'s Yup `required('Please select template name')`.
      required: 'HackerRank template is required',
      // Same precondition as Location + Host: hide until at least one
      // interviewer is picked. Adhoc-HR seeds `location: 'hackerrank'`
      // into the payload at mount, so without the `participant_ids` gate
      // the template would render while Location is still hidden.
      condition: {
        operator: 'and',
        conditions: [
          {
            operator: 'isNotEmpty',
            field: 'participant_ids',
          },
          {
            operator: 'equal',
            field: 'location',
            value: 'hackerrank',
          },
        ],
      },
    },

    // Candidate phone — always mount the block; visibility is gated by
    // the `location === 'phone_interview'` condition on each row. The
    // `isCandidatePhoneEditing` hidden toggle switches between the
    // read-only display and the phone input.
    // Bulk self-schedule fans out to many candidates, so a single phone
    // field has no candidate to bind to — skip the block entirely.
    ...(isBulkSelfSchedule
      ? []
      : [
          {
            type: 'input' as const,
            name: 'isCandidatePhoneEditing',
            hidden: true,
          },
          {
            type: 'custom' as const,
            name: 'candidatePhoneDisplay',
            condition: {
              operator: 'and' as const,
              conditions: [
                {
                  operator: 'equal' as const,
                  field: 'location',
                  value: 'phone_interview',
                },
                {
                  operator: 'notEqual' as const,
                  field: 'isCandidatePhoneEditing',
                  value: 'edit',
                },
              ],
            },
            render: ({
              formMethods,
            }: {
              formMethods: {
                setValue: (_n: string, _v: unknown) => void;
                watch: (_n: string) => unknown;
              };
            }) => (
              <CandidatePhoneField
                candidateName={candidateName}
                candidateAvatar={candidateAvatar}
                phoneNumber={candidatePhone}
                onEdit={() =>
                  formMethods.setValue('isCandidatePhoneEditing', 'edit')
                }
              />
            ),
          },
          {
            type: 'phone' as const,
            name: 'candidate_phone',
            label: 'Candidate phone',
            required: 'Candidate phone is required',
            validate: (value: unknown) => {
              const v = value as
                | { dialCode?: string; number?: string }
                | undefined;
              const num = v?.number?.trim() ?? '';
              if (!num) return 'Candidate phone is required';
              return true;
            },
            condition: {
              operator: 'and' as const,
              conditions: [
                {
                  operator: 'equal' as const,
                  field: 'location',
                  value: 'phone_interview',
                },
                {
                  operator: 'equal' as const,
                  field: 'isCandidatePhoneEditing',
                  value: 'edit',
                },
              ],
            },
            fieldProps: { defaultCountry: 'US' },
          },
        ]),

    hostField,
    officeField,

    {
      // Note-taker only appears when an interviewer is picked AND the
      // location supports it (zoom / google_meet / teams).
      ...noteTakerField,
      condition: {
        operator: 'and',
        conditions: [
          { operator: 'isNotEmpty', field: 'participant_ids' },
          {
            operator: 'in',
            field: 'location',
            value: ['zoom', 'google_meet', 'teams'],
          },
        ],
      },
    },
  ];
}
